import geojson from 'geojson';
import * as I from 'immutable';
import moment from 'moment';
import React from 'react';

import {parseGraphIsoStringsAsDates} from 'app/components/AnalyzePolygonChart/utils';
import {DEFAULT_REPORT_SUBTITLE} from 'app/components/ReportExport/ReportExportTitlePage';
import {ApiFeature, ApiReport, ReportOverviewPage, ReportState} from 'app/modules/Remote/Feature';
import {StateApiNote} from 'app/stores/NotesStore';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as layers from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import * as noteUtils from 'app/utils/noteUtils';

/** What every slide gives the map. */
interface StorySlideBase {
  key: string;
  /** The property part the slide is about; its imagery is what the map draws. */
  feature: I.ImmutableOf<ApiFeature>;
  imageRefs: mapUtils.MapImageRefs;
  /** What the camera frames when the page has no saved bounds. */
  geometry: geojson.GeoJSON;
  mapBounds: geojson.BBox | null;
}

/** The report's cover page: how the story introduces itself. */
export interface CoverSlide extends StorySlideBase {
  kind: 'cover';
  title: string;
  subtitle: string;
  company: string;
  date: string;
  /** The cover description, followed by any overflow pages. */
  paragraphs: string[];
}

export interface NoteSlide extends StorySlideBase {
  kind: 'note';
  title: string;
  note: StateApiNote;
  /** The report page's saved overlay toggles, applied over the note's own overlays. */
  overlayVisibilityByName: Record<string, boolean> | undefined;
  /** Ready for AnalyzePolygonChart. Null when the page hides it or the note has none. */
  chart: {graph: ReturnType<typeof parseGraphIsoStringsAsDates>; areaInM2: number} | null;
  /** Empty when the page hides attachments or the note has none. */
  attachments: string[];
}

export type StorySlide = CoverSlide | NoteSlide;

export interface StorySlides {
  slides: StorySlide[];
  /** Note pages whose note no longer qualifies (archived, missing, or on an unenrolled layer). */
  skippedCount: number;
  /** Note pages left out of a public story because their imagery can't be shared. */
  unshareableCount: number;
}

export const BASEMAP_IMAGE_REFS: mapUtils.MapImageRefs = [{layerKey: layers.NONE, cursor: null}];

export interface StorySlidesArgs {
  report: Pick<ApiReport<ReportState>, 'title' | 'data' | 'isMultilocation' | 'createdAt'>;
  notes: StateApiNote[];
  /** The report's own property. */
  anchorFeature: I.ImmutableOf<ApiFeature>;
  /** Every part of the property, ordered to match `currentPropertyPartIndex`. */
  propertyParts: I.ImmutableOf<ApiFeature[]>;
  organizationName: string;
  enrolledLayerKeys: string[];
  /**
   * For a public story: notes on imagery we can't redistribute are left out, and a cover
   * whose scene is on such imagery shows the basemap instead.
   */
  omitUnshareable?: boolean;
}

/**
 * Turns a saved report into slides: its cover, then one per note, in the order
 * the report itself lists them.
 */
export function makeStorySlides({
  report,
  notes,
  anchorFeature,
  propertyParts,
  organizationName,
  enrolledLayerKeys,
  omitUnshareable = false,
}: StorySlidesArgs): StorySlides {
  const cover = makeCoverSlide({report, anchorFeature, propertyParts, organizationName});
  if (omitUnshareable && !layerUtils.imageRefsAreShareable(cover.imageRefs)) {
    cover.imageRefs = BASEMAP_IMAGE_REFS;
  }
  const {
    slides: noteSlides,
    skippedCount,
    unshareableCount,
  } = makeNoteSlides(
    report.data.notePages ?? [],
    notes,
    propertyParts,
    enrolledLayerKeys,
    omitUnshareable
  );

  return {slides: [cover, ...noteSlides], skippedCount, unshareableCount};
}

/**
 * Field-by-field fallbacks match the printed cover page, except the date: a
 * story of an old report shouldn't claim today's date, so an unset date shows
 * when the report was made.
 */
function makeCoverSlide({
  report,
  anchorFeature,
  propertyParts,
  organizationName,
}: {
  report: StorySlidesArgs['report'];
  anchorFeature: I.ImmutableOf<ApiFeature>;
  propertyParts: I.ImmutableOf<ApiFeature[]>;
  organizationName: string;
}): CoverSlide {
  const coverPage = report.data.coverPage;
  const propertyName = anchorFeature.getIn(['properties', 'name']) as string;
  const partName = anchorFeature.getIn(['properties', 'multiFeaturePartName']) as string | null;
  const defaultTitle =
    !report.isMultilocation && partName ? `${propertyName}, ${partName}` : propertyName;

  const paragraphs = [coverPage?.description ?? '', ...(coverPage?.overflowPages ?? [])]
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  const overview = coverImagery(report.data.overviewPages ?? [], propertyParts);

  return {
    kind: 'cover',
    key: 'cover',
    // The cover borrows the property overview's scene, so both it and its
    // imagery resolve against the same property's feature data.
    feature: overview?.feature ?? anchorFeature,
    imageRefs: overview?.imageRefs ?? BASEMAP_IMAGE_REFS,
    geometry: geoJsonUtils.featureCollection(propertyParts.toJS() as geojson.Feature[]),
    mapBounds: null,
    title: coverPage?.title?.trim() || defaultTitle,
    subtitle: coverPage?.subtitle?.trim() || DEFAULT_REPORT_SUBTITLE,
    company: coverPage?.company?.trim() || organizationName,
    date: coverPage?.date?.trim() || moment(report.createdAt).format('LL'),
    paragraphs,
  };
}

/**
 * The scene of the first visible property overview page that has one. Overview
 * pages don't get slides of their own; this is the only thing the story takes
 * from them.
 */
function coverImagery(
  overviewPages: ReportOverviewPage[],
  propertyParts: I.ImmutableOf<ApiFeature[]>
): {feature: I.ImmutableOf<ApiFeature>; imageRefs: mapUtils.MapImageRefs} | null {
  for (const page of overviewPages) {
    const feature = propertyParts.get(page.currentPropertyPartIndex) ?? propertyParts.first();

    if (page.hidden || !feature) {
      continue;
    }

    if (page.imageRef) {
      return {feature, imageRefs: [page.imageRef]};
    }
    // Pages saved before imageRefs existed carry a bare cursor. Those were always
    // high-res truecolor, and the generic key resolves to whichever source has
    // that scene.
    if (page.cursor) {
      return {
        feature,
        imageRefs: [{layerKey: layers.ANY_TRUECOLOR_HIGH_RES, cursor: page.cursor}],
      };
    }
  }

  return null;
}

function makeNoteSlides(
  notePages: ReportState['notePages'],
  notes: StateApiNote[],
  propertyParts: I.ImmutableOf<ApiFeature[]>,
  enrolledLayerKeys: string[],
  omitUnshareable: boolean
): StorySlides {
  const notesById = new Map(notes.map((note) => [String(note.id), note]));
  const featuresById = new Map(
    propertyParts.toArray().map((feature) => [feature.get('id'), feature])
  );

  const slides: StorySlide[] = [];
  let skippedCount = 0;
  let unshareableCount = 0;

  for (const page of notePages) {
    if (page.hidden) {
      continue;
    }

    const note = notesById.get(String(page.id));
    const feature = note && featuresById.get(note.featureId);

    if (
      !note ||
      !feature ||
      note.isArchived ||
      !noteUtils.isReportableNote(note, enrolledLayerKeys)
    ) {
      skippedCount += 1;
      continue;
    }

    if (omitUnshareable && !layerUtils.imageRefsAreShareable(note.imageRefs)) {
      unshareableCount += 1;
      continue;
    }

    slides.push({
      kind: 'note',
      key: String(note.id),
      note,
      feature,
      title: page.title,
      imageRefs: noteUtils.imageRefsArentBasemap(note.imageRefs)
        ? note.imageRefs
        : BASEMAP_IMAGE_REFS,
      geometry: note.geometry ?? (feature.get('geometry').toJS() as geojson.Geometry),
      mapBounds: page.mapBounds ?? null,
      overlayVisibilityByName: page.overlayVisibilityByName,
      chart:
        !page.chartHidden && note.graph
          ? {
              graph: parseGraphIsoStringsAsDates(note.graph),
              areaInM2: noteUtils.getNoteAreaInM2(note),
            }
          : null,
      attachments: page.attachmentsHidden ? [] : (note.attachments ?? []),
    });
  }

  return {slides, skippedCount, unshareableCount};
}

const NO_SLIDES: StorySlides = {slides: [], skippedCount: 0, unshareableCount: 0};

/** Returns no slides until the report and its property have loaded. */
export function useStorySlides({
  report,
  notes,
  anchorFeature,
  propertyParts,
  organizationName,
  enrolledLayerKeys,
  omitUnshareable = false,
}: Omit<StorySlidesArgs, 'report' | 'anchorFeature'> & {
  report: StorySlidesArgs['report'] | null;
  anchorFeature: I.ImmutableOf<ApiFeature> | null;
}): StorySlides {
  return React.useMemo(
    () =>
      report && anchorFeature
        ? makeStorySlides({
            report,
            notes,
            anchorFeature,
            propertyParts,
            organizationName,
            enrolledLayerKeys,
            omitUnshareable,
          })
        : NO_SLIDES,
    [
      report,
      notes,
      anchorFeature,
      propertyParts,
      organizationName,
      enrolledLayerKeys,
      omitUnshareable,
    ]
  );
}
